import { Currency, CURRENCY_INFO } from '../../constants';
import type { Tier, MilestoneDisplayConfig } from '../../constants/campaign/bmsm-types';
import type { DiscountTierResponse, FreeShippingProgressResponse, ProgressBarResponse } from '../../model';

/**
 * Format a numeric amount for the BMSM banner.
 *
 * - Decimal portion is always dropped (no `.00`).
 * - Values ≥ 1000 are abbreviated with `k` (1 decimal max, trailing `.0` omitted).
 *   e.g., 6,600 → `6.6k`, 220,000 → `220k`, 1,000 → `1k`.
 * - Below 1000, the integer is rendered as-is.
 *   e.g., 800 → `MX$800`, 6,600 → `¥6.6k`, 220,000 → `₮220k`.
 */
function formatCompactAmount(amount: number): string {
  const rounded = Math.round(amount);
  if (Math.abs(rounded) < 1000) {
    return String(rounded);
  }
  const inK = Math.round(amount / 100) / 10;
  return `${inK}k`;
}

export function formatBmsmCurrency(currency: Currency, amount: number): string {
  const formatted = formatCompactAmount(amount);
  const info = CURRENCY_INFO[currency];
  if (!info) return formatted;
  return info.position === 'prefix' ? `${info.symbol}${formatted}` : `${formatted} ${info.symbol}`;
}

export function generateTierTitle(tier: Tier, currency?: Currency): string {
  if (tier.type === 'shipping') {
    return 'Free Shipping';
  }
  if (tier.discountType === 'fixed_cart' && currency) {
    return `${formatBmsmCurrency(currency, tier.discountAmount)} OFF`;
  }
  return `${tier.discountPercent}% OFF`;
}

export interface MessagePart {
  text: string;
  bold: boolean;
}

/**
 * Parse message with bold markers
 *
 * Converts text with **bold markers** into an array of parts with bold flags.
 * Example: "Sign up to unlock **Free Shipping**!"
 * → [{ text: "Sign up to unlock ", bold: false }, { text: "Free Shipping", bold: true }, { text: "!", bold: false }]
 */
export function parseMessageWithBold(message: string): MessagePart[] {
  const parts: MessagePart[] = [];
  const regex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(message)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: message.substring(lastIndex, match.index), bold: false });
    }
    parts.push({ text: match[1], bold: true });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < message.length) {
    parts.push({ text: message.substring(lastIndex), bold: false });
  }

  return parts;
}

/**
 * Map DiscountTierResponse[] to existing Tier[] type.
 * The adapter preserves compatibility with buildMilestonesFromTiers(), calculateProgress(), etc.
 */
export function mapDiscountTiersToTiers(discountTiers: DiscountTierResponse[]): Tier[] {
  return discountTiers
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((tier): Tier => {
      if (tier.discountType === 'fixed_cart') {
        return {
          minAmount: tier.minAmount,
          discountPercent: 0,
          type: 'discount',
          discountType: 'fixed_cart',
          discountAmount: tier.discountAmount,
        };
      }
      return {
        minAmount: tier.minAmount,
        discountPercent: tier.percentage,
        type: 'discount',
        discountType: 'percent',
      };
    });
}

/**
 * Map FreeShippingProgressResponse to a Tier with 'shipping' type.
 */
export function mapFreeShippingToTier(freeShipping: FreeShippingProgressResponse): Tier {
  return {
    minAmount: freeShipping.minAmount,
    discountPercent: 0,
    type: 'shipping',
  };
}

/**
 * Build combined Tier[] from progressBar data.
 * Free shipping tier is placed first (lowest displayOrder), then discount tiers.
 */
export function buildTiersFromProgressBar(params: {
  freeShipping: FreeShippingProgressResponse | null;
  discountTiers: DiscountTierResponse[];
}): Tier[] {
  const { freeShipping, discountTiers } = params;
  const tiers: Tier[] = [];

  if (freeShipping) {
    tiers.push(mapFreeShippingToTier(freeShipping));
  }

  tiers.push(...mapDiscountTiersToTiers(discountTiers));

  return tiers;
}

/**
 * Determine display config for a progressBar tier based on its `applied` field.
 * Replaces the old isAuthenticated/isTopAchieved client-side logic.
 *
 * Three states:
 * - available && applied → active (check icon)
 * - available && !applied → dimmed (dot icon) — minAmount met but combination rules block it
 * - !available → locked (check icon, not achieved)
 */
export function getDisplayConfigForTier(params: {
  available: boolean;
  applied: boolean;
}): MilestoneDisplayConfig {
  const { available, applied } = params;

  if (available && applied) {
    return { active: true, iconVariant: 'check' };
  }

  if (available && !applied) {
    return { active: false, iconVariant: 'dot' };
  }

  return { active: false, iconVariant: 'check' };
}

export interface MilestoneData {
  tier: Tier;
  title: string;
  achieved: boolean;
  statusText: string;
  anchorPercent: number;
  displayConfig: MilestoneDisplayConfig;
  /** Savings amount for this milestone (shipping fee for Free Shipping, cart * discountPercent for discounts) */
  savingsAmount: number;
}

/**
 * UI layout: where milestone icons sit on the progress bar (0-100).
 * 3 tiers: [20, 50, 80] (30-30-30 spacing with 20/20 edges)
 * 2 tiers: [30, 70] (30-40-30 spacing)
 * Other counts fall back to even spacing.
 */
const TIER_ANCHORS_BY_COUNT: Record<number, number[]> = {
  2: [30, 70],
  3: [20, 50, 80],
};

export function getAnchorsForTierCount(count: number): number[] {
  return (
    TIER_ANCHORS_BY_COUNT[count] ??
    Array.from({ length: count }, (_, i) => ((i + 1) / (count + 1)) * 100)
  );
}

function getSavingsAmount(params: { tier: Tier; cartTotalPrice: number }): number {
  const { tier, cartTotalPrice } = params;

  if (tier.type !== 'discount') {
    return 0;
  }

  if (tier.discountType === 'fixed_cart') {
    return tier.discountAmount;
  }

  return (cartTotalPrice * tier.discountPercent) / 100;
}

export function buildMilestonesFromTiers(params: {
  tiers: Tier[];
  progressBar: ProgressBarResponse;
  cartTotalPrice: number;
  currency: Currency;
}): MilestoneData[] {
  const { tiers, progressBar, cartTotalPrice, currency } = params;
  const anchors = getAnchorsForTierCount(tiers.length);

  // tiers are derived from `progressBar` via buildTiersFromProgressBar,
  // so every non-shipping tier is guaranteed to match a discountTiers entry.
  // The optional chain on progressTier covers shipping when freeShipping is null.
  return tiers.map((tier, index) => {
    const progressTier =
      tier.type === 'shipping'
        ? progressBar.freeShipping
        : progressBar.discountTiers.find((t) => t.minAmount === tier.minAmount);

    const achieved = progressTier?.available ?? false;
    const applied = progressTier?.applied ?? false;
    const statusText = achieved ? 'Available' : formatBmsmCurrency(currency, tier.minAmount);
    const anchorPercent = anchors[index] / 100;
    const displayConfig = getDisplayConfigForTier({ available: achieved, applied });
    const savingsAmount = getSavingsAmount({ tier, cartTotalPrice });

    return {
      tier,
      title: generateTierTitle(tier, currency),
      achieved,
      statusText,
      anchorPercent,
      displayConfig,
      savingsAmount,
    };
  });
}

/**
 * Get the index of the next unachieved milestone
 * Returns null if all milestones are achieved
 */
export function getNextMilestoneIndex(milestones: MilestoneData[]): number | null {
  const firstUnachievedIndex = milestones.findIndex((m) => !m.achieved);
  return firstUnachievedIndex === -1 ? null : firstUnachievedIndex;
}

/**
 * Compute progress bar fill (0-100) from progressBar tier state.
 *
 * Each tier carries `minAmount` / `currentAmount` / `available` from the progressBar response.
 * Milestone icon positions come from `anchors` (UI layout, e.g. [20, 50, 80]).
 * Between milestones the bar interpolates linearly using `currentAmount`
 * so bar position always matches the `available` flags.
 *
 * Invariant: `tiers.length === anchors.length`. Callers must pair anchors
 * built via getAnchorsForTierCount(tiers.length) with the same tier list.
 */
type ProgressTierInput = {
  minAmount: number;
  currentAmount: number;
  available: boolean;
};

export function calculateProgress(params: { tiers: ProgressTierInput[]; anchors: number[] }): number {
  const { tiers, anchors } = params;

  if (tiers.length === 0) return 0;
  if (tiers.every((t) => t.available)) return 100;

  const nextIdx = tiers.findIndex((t) => !t.available);
  const next = tiers[nextIdx];

  const prevAnchor = nextIdx === 0 ? 0 : anchors[nextIdx - 1];
  const currAnchor = anchors[nextIdx];
  const prevMinAmount = nextIdx === 0 ? 0 : tiers[nextIdx - 1].minAmount;

  const range = next.minAmount - prevMinAmount;
  if (range <= 0) return currAnchor;

  const ratio = Math.min(1, Math.max(0, (next.currentAmount - prevMinAmount) / range));
  return prevAnchor + ratio * (currAnchor - prevAnchor);
}
